import type { ModelClass } from '@model/Model';
import type { ActiveOrderProps } from '@model/Order';
import type { AxiosResponse } from 'axios';
import { Map } from 'immutable';

export interface ActiveProps<T = any> {
    activeItem?: T;
    activeError?: AxiosResponse;
}

interface Types {
    ACTIVE_SUCCESS: string;
    ACTIVE_CLEAN: string;
    ACTIVE_FAIL: string;
}

interface ActiveItemOptions {
    DEFAULT_STATE: Map<any, any>;
    Model: ModelClass;
    TYPES: Types;
}

export const ActiveItemFactory = (options: ActiveItemOptions) =>
    class ActiveItemClass {
        /**
         * Register aliases
         * @returns {{}}
         */
        fetchAlias() {
            return {
                [options.TYPES.ACTIVE_SUCCESS]: 'onActiveSuccess',
                [options.TYPES.ACTIVE_CLEAN]: 'onActiveClean',
                [options.TYPES.ACTIVE_FAIL]: 'onActiveError',
            };
        }

        /**
         *
         * @param state
         * @param payload
         */
        onActiveSuccess(state = options.DEFAULT_STATE, { payload }) {
            const { Model } = options;
            const { item, items } = payload;
            // @ts-ignore
            const active = new Model();

            active.$fill(item);

            return state.set('active', active).set(
                'items',
                items.map((item) =>
                    // @ts-ignore
                    Model.$instance(item) ? item : new Model().$fill(item),
                ),
            );
        }

        /**
         *
         * @param state
         * @param payload
         */
        onActiveError(state = options.DEFAULT_STATE, { payload }) {
            return state.set('active', null).set('activeError', payload);
        }

        /**
         * @param state
         */
        onActiveClean(state = options.DEFAULT_STATE) {
            return state.set('active', null).set('activeError', null);
        }
    };

interface CreateActiveActionOptions {
    itemProperty: string;
    propsProperty: string;
    stateProperty: string;
    dispatchMethod: any;
    routeParam: string | number;
}

// noinspection SuspiciousTypeOfGuard
/**
 * Create active action, used to fetch an active member of a list
 * @param store
 * @param options
 * @returns {Function}
 * @constructor
 */
export const CreateActiveAction =
    <T = any>(store: string, options: CreateActiveActionOptions) =>
    (props: T & ActiveOrderProps, state = {}) => {
        const {
            itemProperty = 'activeItem',
            stateProperty = 'item',
            dispatchMethod = 'itemActiveRequest',
            propsProperty = 'item',
            routeParam = 'id',
        } = options;

        let dispatchMethod$ = dispatchMethod;
        if (state[stateProperty]) {
            return null;
        }

        if (props.activeError) {
            return {
                error: props.activeError,
                loading: false,
            };
        }

        if (props[itemProperty]) {
            return {
                [stateProperty]: props[itemProperty],
                loading: false,
            };
        }

        if (props[propsProperty] && parseInt(props[propsProperty].id) > 0) {
            return {
                [stateProperty]: props[propsProperty],
                loading: false,
            };
        }

        let item = props[propsProperty];

        if (!item) {
            // @ts-ignore
            const { params } = props.match;
            item = params[routeParam];
        }

        if (typeof item === 'string') {
            item = parseInt(item, 10);
        }

        if (typeof item === 'number') {
            item = {
                id: item,
            };
        }

        // if it's passed as string request will be sent using props[[name]] method
        // noinspection SuspiciousTypeOfGuard
        if (typeof dispatchMethod$ === 'string') {
            dispatchMethod$ = props[dispatchMethod$];
        }

        if (dispatchMethod$ && typeof dispatchMethod$ === 'function') {
            if (item.id > 0) {
                dispatchMethod$(item);
            }

            return {
                loading: true,
            };
        }

        return null;
    };
